/* eslint-disable react-refresh/only-export-components */
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useState,
  useContext,
} from 'react';
import type { UserDetails } from '../models/UserDetails';
import { getToken } from '../apis/LocalStorageApi';
import { decodeToken } from '../utils/constants';

type UserContextType = [
  UserDetails | null,
  Dispatch<SetStateAction<UserDetails | null>>,
  (token: string) => void,
  () => boolean,
];

const UserContext = createContext<UserContextType>([
  null,
  () => {},
  () => {},
  () => false,
]);

function UserProvider({ children }: { children: ReactNode }) {
  const [userDetails, setUserDetails] = useState<UserDetails | null>(() => {
    const token = getToken();
    if (token) {
      return decodeToken(token);
    }
    return null;
  });

  const hasAuth = () => {
    if (!userDetails) return false;
    const currentTime = Date.now() / 1000; // Convert to seconds
    // console.log({ exp: userDetails.exp, currentTime });
    return userDetails.exp > currentTime;
  };

  const setUserFromToken = (token: string) => {
    const userDetails = decodeToken(token);
    setUserDetails(userDetails);
  };
  return (
    <UserContext.Provider
      value={[userDetails, setUserDetails, setUserFromToken, hasAuth]}
    >
      {children}
    </UserContext.Provider>
  );
}

function useUserDetails() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUserDetails must be used within a UserProvider');
  }
  return context;
}

export { UserProvider, UserContext, useUserDetails };
